import * as React from 'react';
import { FocusableElement, tabbable } from 'tabbable';

export interface ListKeyboardNavigationArgs {
  /** Ref for the parent floating element */
  listElementRef: React.RefObject<HTMLElement>;
  /** Ref for the element containing the focusable items */
  childrenWrapperRef: React.RefObject<HTMLElement>;
  /** Function to close the dropdown */
  close: () => void;
  /** Whether the dropdown is open */
  isOpen: boolean;
  /** Whether the dropdown should close when the user navigates past the first or last item */
  closeOnBoundary?: boolean;
  /** Whether pressing Tab or Shift+Tab should move focus into the dropdown */
  handleFocusEntry?: boolean;
}

export const useListKeyboardNavigation = ({
  listElementRef,
  childrenWrapperRef,
  close,
  isOpen,
  closeOnBoundary = true,
  handleFocusEntry = true,
}: ListKeyboardNavigationArgs) => {
  React.useEffect(() => {
    const childrenWrapperNode = childrenWrapperRef.current;
    const listNode = listElementRef.current;

    const arrowHandler = (event: KeyboardEvent) => {
      const tabbableNodes = tabbable(childrenWrapperNode!);

      if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();

        const step = {
          ArrowDown: 1,
          ArrowUp: -1,
        };

        const nodeCount = tabbableNodes.length;
        const focusedChildIndex = tabbableNodes.indexOf(document.activeElement as FocusableElement);
        const newIndex = (focusedChildIndex + (step[event.key] % nodeCount) + nodeCount) % nodeCount;

        tabbableNodes[newIndex]?.focus();
      }
    };

    const listNavigationStartHandler = (event: KeyboardEvent) => {
      if (!childrenWrapperNode || !isOpen || !handleFocusEntry) {
        /*
          If the dropdown is closed, or we don't have a ref for the children
          wrapper, there's nothing for us to do here
        */
        return;
      }
      const pressedTab = event.key === 'Tab' && !event.shiftKey;
      const pressedDownArrow = event.key === 'ArrowDown';

      const tabbableNodes = tabbable(childrenWrapperNode);

      /*
        If the user pressed Tab, or the DownArrow key, and the currently focused
        element is NOT a dropdown item, we bring the focus to the first element
        in the dropdown.

        The check for whether the current active element is a dropdown item is
        necessary because otherwise dropdownNavigationStartHandler and arrowHandler
        will trigger at the same time. This only happens outside of tables, where
        the dropdown items are not rendered within a portal (I think).
      */
      if (
        (pressedTab || pressedDownArrow) &&
        tabbableNodes.indexOf(document.activeElement as FocusableElement) === -1
      ) {
        event.preventDefault();
        const firstListElement = tabbableNodes[0];
        firstListElement?.focus();
      }
    };

    const escapeHandler = (event: KeyboardEvent) => {
      if (!isOpen) {
        /*
          This key event handler only has a job when the dropdown is open
        */
        return;
      }

      const tabbableNodes = tabbable(childrenWrapperNode!);
      const firstListItemCurrentlyFocused = document.activeElement === tabbableNodes[0];
      const lastListItemCurrentlyFocused = document.activeElement === tabbableNodes[tabbableNodes.length - 1];

      if (
        event.key === 'Escape' ||
        (closeOnBoundary && event.key === 'Tab' && !event.shiftKey && lastListItemCurrentlyFocused) ||
        (closeOnBoundary && event.key === 'Tab' && event.shiftKey && firstListItemCurrentlyFocused)
      ) {
        event.preventDefault();
        close();

        /*
            `dropdownNode` is a div, and divs can't receive focus by default, so when the
            dropdown closes we focus on the first focusable element within it.
          */
        tabbable(listNode!)[0]?.focus();
      }
    };

    if (!childrenWrapperNode || !listNode) {
      return;
    }
    listNode.addEventListener('keydown', listNavigationStartHandler);
    listNode.addEventListener('keydown', escapeHandler);
    childrenWrapperNode.addEventListener('keydown', arrowHandler);
    childrenWrapperNode.addEventListener('keydown', escapeHandler);

    return () => {
      listNode.removeEventListener('keydown', listNavigationStartHandler);
      listNode.removeEventListener('keydown', escapeHandler);
      childrenWrapperNode.removeEventListener('keydown', arrowHandler);
      childrenWrapperNode.removeEventListener('keydown', escapeHandler);
    };
  });
};
